import {
  Paper,
  Stack,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { FormDevtool } from 'components/FormDevtool'
import { isValid } from 'date-fns'
import dayjs from 'dayjs'
import { formattedTimeStandart } from 'hooks/date/formattedTime'
import { Dispatch, SetStateAction, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQueryState } from 'react-router-use-location-state'
import { ETenderStatus } from 'types/tender'
import { initialFilters } from '.'

export type TenderSearchFilters = {
  name: string
  orgName: string
  startFrom: string | null
  startUntil: string | null
  status: Array<string>
}

type Props = {
  setFilters: Dispatch<SetStateAction<TenderSearchFilters>>
  defaultValues: TenderSearchFilters
  forInnerSearch?: boolean
}

export const TenderSearchFilter = ({
  setFilters,
  defaultValues,
  forInnerSearch,
}: Props) => {
  const { t } = useTranslation()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_page, setPage] = useQueryState('page', 0)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { dirtyFields },
  } = useForm({
    defaultValues,
  })

  const allValues = watch()
  const tenderStatus = watch('status')

  useEffect(() => {
    if (tenderStatus && dirtyFields.status) {
      onSubmit(allValues)
    }
  }, [tenderStatus, dirtyFields])

  const resetFilters = () => {
    setFilters(initialFilters)
    reset(initialFilters)
    setValue('startFrom', null)
    setValue('startUntil', null)
  }

  const onSubmit = (values: TenderSearchFilters) => {
    setPage(0)
    setFilters(values)
  }

  return (
    <Paper sx={{ p: 3 }}>
      <FormDevtool control={control} />
      <Stack spacing={3}>
        <Stack direction="row" spacing={2}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t('search-based-on-name')}
                size="small"
                variant="outlined"
              />
            )}
          />
          <Controller
            name="orgName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label={t('organization-name')}
                variant="outlined"
              />
            )}
          />
          {!forInnerSearch && (
            <FormControl fullWidth margin="normal">
              <InputLabel size="small" id="tender-status">
                {t('status')}
              </InputLabel>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <Select
                    {...field}
                    id="tender-status"
                    size="small"
                    label={t('status')}
                  >
                    {Object.keys(ETenderStatus).map((measurement) => (
                      <MenuItem value={measurement} key={measurement}>
                        {t(`tender-status-${measurement}`)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          )}
        </Stack>
        <Stack direction="row" spacing={2}>
          <Controller
            name="startFrom"
            control={control}
            render={({ field }) => (
              <DatePicker
                {...field}
                value={field.value}
                inputRef={field.ref}
                label={t('startDate')}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  },
                  field: { clearable: true },
                }}
                onChange={(date) => {
                  if (
                    date !== null &&
                    isValid(new Date(formattedTimeStandart(date)))
                  ) {
                    const result = dayjs(date).toISOString()
                    field.onChange(result)
                  }
                }}
              />
            )}
          />
          <Controller
            name="startUntil"
            control={control}
            render={({ field }) => (
              <DatePicker
                {...field}
                value={field.value}
                inputRef={field.ref}
                label={t('endDate')}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  },
                  field: { clearable: true },
                }}
                onChange={(date) => {
                  if (
                    date !== null &&
                    isValid(new Date(formattedTimeStandart(date)))
                  ) {
                    const result = dayjs(date).toISOString()
                    field.onChange(result)
                  }
                }}
              />
            )}
          />
        </Stack>

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={resetFilters}>
            {t('clear')}
          </Button>
          <Button
            variant="contained"
            style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
            onClick={handleSubmit(onSubmit)}
          >
            {t('search')}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  )
}
